import GridPage from "components/layout/GridPage";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Checkbox from "components/common/Checkbox";
import { useContext, useEffect, useState, useMemo } from "react";
import useIsMobile from "components/hooks/useIsMobile";
import { useGenerateReport, useListReports } from "services/reports";
import {
  GenerateReportResponse,
  Report,
  ReportColumn,
  ReportParameter,
} from "protogen/reports_service_pb";
import { DatePicker } from "@mui/x-date-pickers";
import FamilySelect from "components/creation/FamilySelect";
import { useNavigate, useParams } from "react-router-dom";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ReportChart } from "components/common/reporting/ReportCharts";
import HighlightedReports from "components/common/reporting/HighlightedReports";
import {
  ChartBar,
  ChartLine,
  ChartPie,
  FileSpreadsheet,
  Maximize2,
} from "lucide-react";
import AdminFamilySelect from "../../components/creation/AdminFamilySelect";
import { CurrentUserContext } from "../../components/context/RequireAuth";
import { isUserAdmin } from "../../common/userUtils";
import ScheduledJobsList from "components/scheduling/ScheduledJobsList";

const parseMarkdownLink = (markdownLink: string): [string, string] => {
  const regex = /\[([^\]]+)\]\(([^)]+)\)/;
  const match = markdownLink.match(regex);

  if (match && match.length === 3) {
    return [match[1], match[2]];
  }

  return ["", ""];
};

const getFormattedDate = (reportName: string): string => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0"); // January is 0!
  const yy = String(today.getFullYear()).slice(-2);
  return `${reportName.replaceAll(/\s/g, "")}.${yy}${mm}${dd}.csv`;
};

const convertToCSV = (response: GenerateReportResponse) => {
  const escapeValue = (value: string): string => {
    if (
      value.includes(",") ||
      value.includes('"') ||
      value.includes("\n") ||
      value.includes("\r")
    ) {
      value = value.replace(/"/g, '""');
      return `"${value}"`;
    }
    return value;
  };
  const escapeCell = (value: string, col: ReportColumn): string => {
    if (col.type === "url") {
      const [label] = parseMarkdownLink(value);
      return escapeValue(label);
    } else {
      return escapeValue(value);
    }
  };

  const rows = [
    response.columns.map((c) => escapeValue(c.name)).join(","),
    ...response.rows.map((row) =>
      row.values
        .map((c, idx) => escapeCell(c, response.columns[idx]))
        .join(","),
    ),
  ];
  return rows.join("\r\n");
};

const downloadCSV = (report: Report, response: GenerateReportResponse) => {
  const csvData = convertToCSV(response);
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", getFormattedDate(report.name));
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type ReportsSelectProps = {
  value: string;
  loading: boolean;
  error: boolean;
  setValue: (value: string) => void;
  options: Record<string, { label: string; value: string; type?: string }>;
};
const ReportsSelect = ({
  value,
  loading,
  error,
  setValue,
  options,
}: ReportsSelectProps) => {
  const optionsArray = Object.values(options).sort((a, b) => {
    return a.label.localeCompare(b.label);
  });
  const selectedOption = options[value] || null;

  // Function to determine the icon based on report type
  const getReportIcon = (type: string | undefined) => {
    switch (type) {
      case "bar":
        return <ChartBar />;
      case "line":
        return <ChartLine />;
      case "pie":
        return <ChartPie />;
      default:
        return <FileSpreadsheet />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
        <Autocomplete
          sx={{ width: "100%", cursor: "pointer" }}
          options={optionsArray}
          getOptionLabel={(option) => option.label}
          loading={loading}
          disabled={loading}
          value={selectedOption}
          onChange={(event, newValue) => {
            if (newValue) {
              setValue(newValue.value);
            } else {
              setValue("");
            }
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {getReportIcon(option.type)}
              <Box sx={{ ml: 1 }}>{option.label}</Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Reports"
              error={error}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedOption ? (
                  <InputAdornment
                    position="start"
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      margin: "16px 2px 0 10px",
                    }}
                  >
                    {getReportIcon(selectedOption?.type)}
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

const AdvisorStatusSelect = ({
  status = "active",
  setStatus,
}: {
  status?: string;
  setStatus: (s: string) => void;
}) => {
  return (
    <FormControl fullWidth>
      <InputLabel id="select-label">Status</InputLabel>
      <Select
        fullWidth
        labelId="select-label"
        id="select-label"
        value={status}
        label="Status"
        onChange={(e) => setStatus(e.target.value)}
      >
        <MenuItem value="all">All</MenuItem>
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="deactivated">Deactivated</MenuItem>
        <MenuItem value="test">Test</MenuItem>
        <MenuItem value="staff">Staff</MenuItem>
        <MenuItem value="pending">Pending</MenuItem>
      </Select>
    </FormControl>
  );
};

const ReportData = ({ report }: { report: GenerateReportResponse }) => {
  const columns = useMemo(() => {
    return report.columns.map((column, idx) => {
      const field = `field${idx}`;
      let type: GridColDef["type"] = "string";
      let valueFormatter: GridColDef["valueFormatter"];
      let renderCell: GridColDef["renderCell"];

      if (column.type === "number") {
        type = "number";
        valueFormatter = (val) => {
          const value = parseFloat(val);
          if (isNaN(value)) {
            return "";
          }
          return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
        };
      } else if (column.type === "percentage") {
        type = "number";
        valueFormatter = (val) => {
          const value = parseFloat(val);
          if (isNaN(value)) {
            return "";
          }
          return `${(100 * value).toFixed(1)}%`;
        };
      } else if (column.type === "currency") {
        type = "number";
        valueFormatter = (val) => {
          const value = parseFloat(val);
          if (isNaN(value)) {
            return "";
          }
          return value.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          });
        };
      } else if (column.type === "date") {
        type = "date";
        valueFormatter = (val) => {
          const date = new Date(val);
          return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US");
        };
      } else if (column.type === "url") {
        type = "string";
        renderCell = (params: GridRenderCellParams<any>) => {
          const [label, url] = parseMarkdownLink(params.value);
          return (
            <Link href={url} target="_blank" rel="noopener">
              {label}
            </Link>
          );
        };
      }

      return {
        field,
        headerName: column.name,
        type,
        sortable: true,
        valueFormatter,
        renderCell,
        editable: false,
        flex: 1, // Add flex to make columns expand
      };
    });
  }, [report]);

  const rows = useMemo(() => {
    return report.rows.map((row, rowIndex) => {
      const rowData: { id: number } & { [key: string]: any } = { id: rowIndex };
      row.values.forEach((value, colIndex) => {
        rowData[`field${colIndex}`] = value;
      });
      return rowData;
    });
  }, [report]);

  return (
    <Box sx={{ width: "100%" }}>
      <DataGrid
        columns={columns}
        rows={rows}
        autoHeight
        sortingOrder={["asc", "desc"]}
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-cell": {
            userSelect: "none",
          },
          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
          },
        }}
      />
    </Box>
  );
};

const DateSelect = ({
  label,
  disabled,
  value,
  update,
  error,
  format,
  views,
  openTo,
}: {
  label: string;
  disabled: boolean;
  value?: Date;
  update: (d: Date | null) => void;
  error?: string | null;
  format?: string | null;
  views?: Array<"year" | "month" | "day">;
  openTo?: "year" | "month" | "day";
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  return (
    <FormControl sx={{ width: "100%" }}>
      <DatePicker
        disabled={disabled}
        label={label}
        value={value}
        sx={{ width: "100%", ".MuiButtonBase-root": { marginRight: "unset" } }}
        onChange={update}
        format={format ? format : "MM/dd/yyyy"}
        views={views}
        openTo={openTo}
        open={dateOpen}
        onClose={() => setDateOpen(false)}
        slotProps={{
          textField: {
            error: !!error,
            helperText: error,
            onClick: () => setDateOpen(true),
          },
        }}
      />
    </FormControl>
  );
};

const GenerationParameter = ({
  paramDefinition,
  parameters,
  loading,
  setValue,
}: {
  paramDefinition: ReportParameter;
  parameters: Record<string, any>;
  loading: boolean;
  setValue: (v: Record<string, any>) => void;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  if (paramDefinition.type === "date" || paramDefinition.type === "month") {
    const isMonth = paramDefinition.type === "month";
    return (
      <DateSelect
        label={paramDefinition.label}
        value={parameters[paramDefinition.name] || undefined}
        disabled={loading}
        format={isMonth ? "MM/yyyy" : undefined}
        views={isMonth ? ["year", "month"] : undefined}
        openTo={isMonth ? "month" : undefined}
        update={(d) => {
          setValue({ [paramDefinition.name]: d });
        }}
      />
    );
  }
  if (paramDefinition.type === "advisor-status") {
    return (
      <AdvisorStatusSelect
        status={parameters[paramDefinition.name] || undefined}
        setStatus={(v) => {
          setValue({ [paramDefinition.name]: v });
        }}
      />
    );
  }
  if (paramDefinition.type === "boolean") {
    const checked = parameters[paramDefinition.name] !== "0";
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            disabled={loading}
            onChange={(_, checked) => {
              setValue({ [paramDefinition.name]: checked ? "1" : "0" });
            }}
          />
        }
        label={paramDefinition.label}
      />
    );
  }
  if (paramDefinition.type === "choice") {
    return (
      <Select
        fullWidth
        title={paramDefinition.label}
        placeholder={paramDefinition.label}
        value={parameters[paramDefinition.name] || undefined}
        onChange={(e) => {
          setValue({ [paramDefinition.name]: e.target.value });
        }}
      >
        {paramDefinition.choices.map((choice) => (
          <MenuItem key={choice} value={choice}>
            {choice}
          </MenuItem>
        ))}
      </Select>
    );
  }
  if (paramDefinition.type === "family") {
    if (isAdmin) {
      return (
        <AdminFamilySelect
          disabled={loading}
          selectedFamily={parameters[paramDefinition.name] || null}
          onChange={(v) => {
            setValue({ [paramDefinition.name]: v });
          }}
        />
      );
    } else {
      return (
        <FamilySelect
          disabled={loading}
          selectedFamily={parameters[paramDefinition.name] || null}
          onChange={(v) => {
            setValue({ [paramDefinition.name]: v });
          }}
        />
      );
    }
  }
  return null;
};

const updateParametersForReport = (
  report: Report,
  parameters: Record<string, any>,
): Record<string, any> => {
  const keepKeys = new Set(report.explicitParameters.map((p) => p.name));
  const newParams = Object.keys(parameters)
    .filter((k: string) => keepKeys.has(k))
    .map((k) => [k, parameters[k]])
    .reduce(
      (obj, [key, value]) => {
        obj[key] = value;
        return obj;
      },
      {} as Record<string, any>,
    );
  for (const paramDefinition of report.explicitParameters) {
    if (paramDefinition.defaultValue && !newParams[paramDefinition.name]) {
      if (paramDefinition.type === "date" || paramDefinition.type === "month") {
        const now = new Date();
        const [part, transform] = paramDefinition.defaultValue.split(":");
        if (part === "day") {
          now.setTime(
            now.getTime() + 1000 * 60 * 60 * 24 * parseInt(transform),
          );
        } else if (part === "month") {
          if (paramDefinition.type === "month") {
            now.setDate(1);
          }
          now.setMonth(now.getMonth() + parseInt(transform));
        } else if (part === "year") {
          now.setFullYear(now.getFullYear() + parseInt(transform));
        }
        newParams[paramDefinition.name] = now;
      } else {
        newParams[paramDefinition.name] = paramDefinition.defaultValue;
      }
    }
  }
  return newParams;
};

export default () => {
  const isMobile = useIsMobile();
  let params = useParams();
  const navigate = useNavigate();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const { request: generate, loading: generating, error } = useGenerateReport();
  const [report, setReport] = useState<Report | null>(null);
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [reportData, setReportData] = useState<GenerateReportResponse | null>(
    null,
  );
  // State variable to control the expandable area
  const [dataExpanded, setDataExpanded] = useState(false);
  const {
    request: listReports,
    data: reports,
    loading: listing,
    error: listError,
  } = useListReports();

  useEffect(() => {
    listReports();
  }, []);

  // Keep the selected report in sync with the URL so browser back/forward
  // works after a report has been selected.
  useEffect(() => {
    const refFromUrl = decodeURIComponent(params.reportName || "");
    if (refFromUrl === (report?.ref || "")) return;
    if (!refFromUrl) {
      setReport(null);
      setReportData(null);
      setDataExpanded(false);
      return;
    }
    const urlReport =
      (reports?.reports || []).find((r) => r.ref === refFromUrl) || null;
    if (urlReport) {
      setReport(urlReport);
      setParameters((p) => updateParametersForReport(urlReport, p));
      setReportData(null);
      setDataExpanded(false);
    }
  }, [params.reportName, reports, report]);

  const isValid = () => {
    if (report === null) return false;
    for (const param of report.explicitParameters) {
      if (parameters[param.name] === undefined) return false;
    }
    return true;
  };

  const onSubmit = async () => {
    if (isValid()) {
      const paramsForRequest = { ...parameters };
      report?.explicitParameters.forEach((p) => {
        if (p.type === "boolean") {
          paramsForRequest[p.name] =
            paramsForRequest[p.name] === "0" ? "0" : "1";
        }
      });
      const resp = await generate({
        reportRef: report?.ref || "",
        parameters: paramsForRequest,
      });
      setReportData(resp);
    }
  };

  const loading = generating || listing;
  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "64px auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "0",
        alignItems: "start",
      }}
    >
      <Typography variant="title" id="tableTitle">
        Generate reports
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "600px",
          gap: 2,
          minWidth: "300px",
          marginTop: "30px",
          width: "100%",
        }}
      >
        <ReportsSelect
          loading={loading}
          error={!!listError}
          value={report?.ref || ""}
          setValue={(value) => {
            const newReport =
              (reports?.reports || []).find((report) => report.ref === value) ||
              null;
            if (newReport && report?.ref !== newReport.ref) {
              setParameters((r) => updateParametersForReport(newReport, r));
            } else if (newReport) {
              setParameters({});
            }
            setReport(newReport);
            setReportData(null);
            setDataExpanded(false);
            navigate(
              "/tools/reports/" + encodeURIComponent(newReport?.ref || ""),
              {
                replace: true,
              },
            );
          }}
          options={(reports?.reports || []).reduce(
            (
              acc: Record<
                string,
                { label: string; value: string; type?: string; key?: string }
              >,
              report,
            ) => {
              acc[report.ref] = {
                label: report.name,
                value: report.ref,
                type: report?.chartDefinitions[0]?.type,
                key: report.ref,
              };
              return acc;
            },
            {},
          )}
        />
        {report && (
          <>
            <Alert severity="info" variant="outlined">
              <AlertTitle>{report.name}</AlertTitle>
              {report.description}
            </Alert>
            {isAdmin && (
              <ScheduledJobsList
                reportId={report.reportId}
                report={report}
                parameters={parameters}
              />
            )}
            {report.explicitParameters.map((paramDefinition) => (
              <GenerationParameter
                key={paramDefinition.name}
                paramDefinition={paramDefinition}
                parameters={parameters}
                loading={loading}
                setValue={(v) => {
                  setParameters((p) => ({ ...p, ...v }));
                }}
              />
            ))}
          </>
        )}
        {listError && !listing && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => listReports()}
              >
                Retry
              </Button>
            }
          >
            <AlertTitle>Error</AlertTitle>
            There was an error loading the list of reports.
          </Alert>
        )}
        {error && !generating && (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            There was an error generating your report. Please try again.
          </Alert>
        )}
        <Button onClick={onSubmit} disabled={loading || !isValid()}>
          Generate Report
        </Button>
      </Box>
      {!report && !loading && reports?.reports && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Popular Reports
          </Typography>
          <HighlightedReports
            reports={(reports?.reports || []).reduce(
              (acc, report) => {
                acc[report.ref] = {
                  label: report.name,
                  value: report.ref,
                  type: report?.chartDefinitions[0]?.type,
                };
                return acc;
              },
              {} as Record<
                string,
                { label: string; value: string; type?: string }
              >,
            )}
            onSelect={(ref) => {
              const newReport =
                reports.reports.find((r) => r.ref === ref) || null;
              if (newReport) {
                setReport(newReport);
                setParameters((r) => updateParametersForReport(newReport, r));
                navigate("/tools/reports/" + ref);
              }
            }}
          />
        </>
      )}
      {report && reportData && !generating && (
        <Box
          sx={{
            marginTop: "36px",
            overflowX: "scroll",
            width: "100%",
            ...(!isMobile ? { minWidth: "600px" } : {}),
          }}
        >
          {report.chartDefinitions[0]?.type ? (
            <>
              <ReportChart report={report} reportData={reportData} />
              {!dataExpanded && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    padding: "5px 25px",
                    marginTop: "25px",
                    borderColor: "rgb(224, 224, 224)",
                    borderWidth: "2px",
                    borderStyle: "solid",
                    borderRadius: "4px",
                  }}
                >
                  <Button
                    variant="text"
                    onClick={() => setDataExpanded(true)}
                    startIcon={<Maximize2 />}
                  >
                    Show Data
                  </Button>
                </Box>
              )}
            </>
          ) : null}
          {(dataExpanded || !report.chartDefinitions[0]?.type) && (
            <>
              <ReportData report={reportData} />
              <Box
                justifyContent="start"
                flexDirection="row"
                sx={{
                  display: reportData.rows.length === 0 ? "none" : "flex",
                  marginTop: "24px",
                  width: "100%",
                }}
                gap={"16px"}
              >
                <Button onClick={() => downloadCSV(report, reportData)}>
                  Download
                </Button>
                <Button
                  onClick={() =>
                    navigator.clipboard.writeText(convertToCSV(reportData))
                  }
                  variant="outlined"
                >
                  Copy to Clipboard
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </GridPage>
  );
};
